import handRaisedWithoutWaving from 'assets/images/characters/CarolinaMariadeJesus/hand-raised-without-waving.webp';
import {filterAllowedUnicodeCharsFromText} from 'components/BasicEditor/lib/editorUtils';
import CompositionHeader from 'containers/EditorPage/components/CompositionHeader';
import PageLoading from 'components/PageLoading';
import ChatBubble from 'containers/CompositionFeedback/ChatBubble';
import Dialog from 'containers/CompositionFeedback/Dialog';
import CompositionSidebar from 'containers/EditorPage/CompositionSidebar';
import useCompositionNavegation from 'features/useCompositionNavegation';
import useLearningPathNavigation from 'features/useLearningPathNavigation';
import {useCallback, useEffect, useRef, useState} from 'react';
import {connect} from 'react-redux';
import {useNavigate, useParams, useLocation} from 'react-router-dom';
import Textarea from 'react-textarea-autosize';
import {
  fetchCompositionByIdRequest,
  fetchCompositionJsonStatsAsyncRequest,
  getCompositionById,
  getIsLoadingCompositionById,
  isLoadingCompositionsJsonStatsAsync,
  writeCompositionRequest,
} from 'store/reducers/compositions';
import {
  changeToggleCompletedTestReportStatus,
  getToggleStatus,
  SelectedToggle,
} from 'store/reducers/toggle';
import {ApplicationState} from 'store/rootReducer';
import checkNetworkStatus from 'utils/functions/checkNetworkStatus';
import {counter} from 'utils/functions/counter';
import {timerSetup} from 'utils/functions/timer';
import {useMobile, usePrevious, useQueryParam} from 'utils/hooks';
import useIndexedDB from 'utils/hooks/useIndexedDB';
import useTrackEvent from 'utils/hooks/useTrackEvent';
import {EventData} from 'utils/hooks/useTrackEvent/types';
import {CompositionDetailExtended} from 'utils/types/compositions';
import GeneralErrorModal from 'components/GeneralErrorModal';
import {WritePreparation} from 'APIs/letrusAPI/writePreparation/types';
import {uploadCompositionRequest} from 'APIs/letrusAPI/composition';
import getFeatureFlag from 'utils/functions/getFeatureFlags';
import {useModals} from 'letrus-ui';
import {isFocusModeFeedbackReleased, useFocusMode} from 'contexts/FocusMode';
import useFocusModeRedirect from 'features/useFocusModeRedirect';
import SidebarButtons from '../../components/SidebarButtons';
import AiUsageAlert from './components/AiUsageAlert';
import CompositionInfo from './CompositionInfo';
import styles from './EditorPage.module.scss';
import FinishCompositionModal from './Modals/FinishCompositionModal/FinishCompositionModal';
import ILetrusModal from './Modals/ILetrusModal';
import RecoverCompositionModal from './Modals/RecoverCompositionModal';
import OptionalRewriteModal from './Modals/OptionalRewriteModal';
import SubmitCompositionModal from './Modals/SubmitCompositionModal';
import SubmitOCRModal from './Modals/SubmitOCRModal';

export interface UserComposition {
  id?: number | string;
  title?: string;
  text?: string;
  modified?: number;
  version?: number;
  finished?: boolean;
}

interface DispatchProps {
  writeCompositionRequest: typeof writeCompositionRequest;
  fetchCompositionByIdRequest: typeof fetchCompositionByIdRequest;
  changeToggleCompletedTestReportStatus: typeof changeToggleCompletedTestReportStatus;
  fetchCompositionJsonStatsAsyncRequest: typeof fetchCompositionJsonStatsAsyncRequest;
}

interface StateProps {
  isLoadingCompositionsJsonStatsAsync: boolean;
  isLoadingCompositionById: boolean;
  composition: ImmutableMap<CompositionDetailExtended>;
  toggleStatus: SelectedToggle;
}

const MAX_EDITOR_NUMBER_OF_CHARACTERS = 10000;

export type EditorPageProps = StateProps & DispatchProps;

const EditorPage: React.FC<EditorPageProps> = ({
  composition,
  writeCompositionRequest,
  isLoadingCompositionById,
  fetchCompositionByIdRequest,
  changeToggleCompletedTestReportStatus,
  fetchCompositionJsonStatsAsyncRequest,
  isLoadingCompositionsJsonStatsAsync,
  toggleStatus,
}) => {
  const [userComposition, setUserComposition] = useState<UserComposition>();
  const [autoSaveTimer, setAutoSaveTimer] = useState<NodeJS.Timeout>();
  const [showLoader, setShowLoader] = useState(true);
  const [reloadingCompositionId, setReloadingCompositionId] =
    useState<string | undefined>();
  const [hasFinishedLocalComposition, setHasFinishedLocalComposition] =
    useState<boolean | undefined>(false);
  const [isOpenReminderCompositionReview, setIsOpenReminderCompositionReview] =
    useState<boolean>(false);

  const previousIsLoadingCompositionById = usePrevious(
    isLoadingCompositionById,
  );

  const navigate = useNavigate();
  const location = useLocation();
  const {composition_id} = useParams();
  const {isMobile, getAccessSource} = useMobile();
  const {trackEvent, debouncedTrackEvent} = useTrackEvent();
  const {redirectCompositionByStatus} = useCompositionNavegation({composition});
  const compositionsIDB = useIndexedDB<UserComposition>('LocalCompositions');
  const writePreparationIDB = useIndexedDB<WritePreparation>(
    'LocalWritePreparations',
  );
  const backspaceCounter = counter();
  const localVersionCounter = counter();
  const queryParams = useQueryParam();
  const learningPathUserInstanceId = queryParams.get('trilha') ?? '';
  const {learningPath, navigateToLearningPath} = useLearningPathNavigation(
    learningPathUserInstanceId,
  );
  const {handleFocusModeRedirect} = useFocusModeRedirect({learningPath});
  const {
    isActive,
    remainingSeconds,
    effectiveTimeSeconds,
    isExpired,
    isOffline,
    registerExpireCallback,
    registerReconnectCallback,
  } = useFocusMode();

  const isActiveRef = useRef(isActive);
  isActiveRef.current = isActive;

  const editorIsEmpty = !userComposition?.text?.trim();

  const iLetrusType = composition.getIn(['test', 'template', 'result']);
  const isDynamicEditor =
    composition.getIn(['test', 'template', 'editor']) === 'dynamic';
  const isCompositionFinished =
    composition.get('status') === 'finished' ||
    composition.get('flag_finished') ||
    !!userComposition?.finished;

  const isReviewGridMultigenre =
    composition.getIn(['instruction_id', 'genre', 'review_grid']) === 61;
  const isFuvest =
    composition.getIn(['instruction_id', 'genre', 'review_grid']) === 2;

  const eventDataRef = useRef<EventData>({});
  const {current: userCompositionRef} = useRef<UserComposition>();
  const sessionTimerRef = useRef<ReturnType<typeof timerSetup>>();
  const hasTrackedTimeRef = useRef(false);
  const expireCallbackStateRef = useRef({
    userComposition,
    isCompositionFinished,
    isFuvest,
  });

  const uploadComposition = uploadCompositionRequest();

  const shouldBlockCopyPasteActions = getFeatureFlag('disable_copy_paste').isOn;

  const modals = useModals([
    'iLetrus',
    'submitOCR',
    'confirmation',
    'saveComposition',
    'recoverComposition',
    'saveCompositionError',
    'optionalRewrite',
  ]);

  const trackTimeSpent = useCallback(() => {
    if (
      hasTrackedTimeRef.current ||
      !sessionTimerRef.current ||
      !eventDataRef.current.composition?.composition_id
    ) {
      return;
    }

    hasTrackedTimeRef.current = true;

    trackEvent('time_spent_with_open_editor', {
      ...eventDataRef.current,
      time: {
        spent_time: sessionTimerRef.current.finish(),
      },
    });
  }, [trackEvent]);

  expireCallbackStateRef.current = {
    userComposition,
    isCompositionFinished,
    isFuvest,
  };

  // Register Focus Mode expire callback: uploads composition silently when timer expires
  useEffect(() => {
    if (!isActive) return undefined;

    const unregister = registerExpireCallback(async () => {
      const {
        userComposition: currentComposition,
        isCompositionFinished: currentFinished,
        isFuvest: currentIsFuvest,
      } = expireCallbackStateRef.current;

      if (currentFinished) return;

      stopAutoSave();
      const accessSource = await getAccessSource();
      saveOffline({...currentComposition, finished: true});

      await uploadComposition.trigger({
        id: Number(composition_id),
        composition_source: accessSource,
        title: currentComposition?.title ?? '',
        composition_raw: currentComposition?.text ?? '',
        review_type: 'AI-Structure',
        uses_ai_comp:
          composition.getIn(['test', 'uses_ai_comp']) || currentIsFuvest,
      });
    });

    return unregister;
  }, [isActive, composition_id]);

  // Register Focus Mode reconnect callback: flushes in-memory composition content
  // to the server when the student reconnects before the timer expires.
  // Uses expireCallbackStateRef (updated every render) to avoid stale closure.
  // Does not call saveComposition() to avoid stopping the auto-save timer.
  useEffect(() => {
    if (!isActive) return undefined;

    const unregister = registerReconnectCallback(async () => {
      const {
        userComposition: currentComposition,
        isCompositionFinished: currentFinished,
      } = expireCallbackStateRef.current;

      if (!currentComposition || currentFinished) return;

      const accessSource = await getAccessSource();
      writeCompositionRequest({
        id: composition_id,
        title: currentComposition.title ?? '',
        composition_raw: currentComposition.text ?? '',
        composition_source: accessSource,
        step_number: learningPath?.step_number ?? null,
      });
    });

    return unregister;
  }, [isActive, composition_id]);

  useEffect(() => {
    fetchCompositionByIdRequest(composition_id);

    sessionTimerRef.current = timerSetup();

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      trackTimeSpent();
      onBeforeUnload(event);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        trackTimeSpent();
      } else if (document.visibilityState === 'visible') {
        hasTrackedTimeRef.current = false;
        sessionTimerRef.current = timerSetup();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      // save the composition on IDB on page unmount
      if (userCompositionRef && !userCompositionRef.finished) {
        saveComposition(userCompositionRef);
      }

      // fire the backspace event on page unmount
      // if any backspace was pressed
      if (backspaceCounter.total() > 0) {
        trackEvent('pressed_backspace', {
          ...eventDataRef.current,
          count: {action_count: backspaceCounter.total()},
        });
      }

      trackTimeSpent();

      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    compositionsIDB?.getAll()?.then((localCompositions) => {
      const finishedLocalComposition = localCompositions?.filter(
        (finishedComposition) => {
          return (
            finishedComposition.id === String(composition?.get('id')) &&
            finishedComposition.finished
          );
        },
      );

      setHasFinishedLocalComposition(finishedLocalComposition?.[0]?.finished);
    });

    checkNetworkStatus({
      offlineCallback: () => {
        if (hasFinishedLocalComposition && isReviewGridMultigenre) {
          navigate({
            pathname: `/atividades/escrita/${composition_id}/devolutiva/apresentacao`,
            search: location.search,
          });
        }
      },
    });
  }, [hasFinishedLocalComposition]);

  useEffect(() => {
    if (previousIsLoadingCompositionById && !isLoadingCompositionById) {
      const isCompositionWithPreparation =
        composition.get('status') === 'preparing' &&
        composition.get('uses_write_preparation');

      const isCompositionReviewed = composition.get('status') === 'reviewed';
      const shouldRedirectToReview =
        isCompositionReviewed || !composition.get('uses_ai_comp');

      const handled = handleFocusModeRedirect();
      if (handled) {
        setShowLoader(false);
        return;
      }

      if (isCompositionWithPreparation) {
        writePreparationIDB
          .getItem(String(composition.get('id')))
          .then((writePreparation) => {
            if (writePreparation?.status === 'finished') {
              loadComposition();
              eventDataRef.current = {
                test: {test_id: Number(composition.get('test_id'))},
                composition: {composition_id: composition.get('id')},
                learningPathUserInstance: {
                  learning_path_user_instance_id: learningPath?.id,
                  step_id: composition.getIn(['step', 'id']),
                },
              };
            } else {
              redirectCompositionByStatus();
            }
          });
        return;
      }

      if (shouldRedirectToReview) {
        redirectCompositionByStatus();
      }

      loadComposition();
      eventDataRef.current = {
        test: {test_id: Number(composition.get('test_id'))},
        composition: {composition_id: composition.get('id')},
        learningPathUserInstance: {
          learning_path_user_instance_id: learningPath?.id,
          step_id: composition.getIn(['step', 'id']),
        },
      };

      showOptionalRewriteModal();
    }
  }, [isLoadingCompositionById, previousIsLoadingCompositionById]);

  // On reopening (Focus Mode), the editor can mount before the reopened
  // composition_raw lands in the store. The loading-edge effect above runs
  // loadComposition() with a stale `composition` from its closure (it depends
  // only on the loading flags, not on `composition`), so the cached text is not
  // rendered until a full reload (F5). Sync the editor to composition_raw when
  // it arrives for a composition the editor has not loaded yet. The id guard
  // (userComposition.id !== composition.id) ensures this only runs on the
  // initial load for that composition, never overwriting the student's typing.
  // We go through loadAPIComposition (instead of setUserComposition directly)
  // so this path honors compareOnlineOfflineComposition() the same way the
  // normal load does: if a newer offline draft exists in IndexedDB, the recover
  // modal still fires rather than being silently overwritten by the server text.
  useEffect(() => {
    const compositionRaw = composition.get('composition_raw');
    const compositionId = composition.get('id');

    const isEditorLoadedForThisComposition =
      userComposition?.id === compositionId;

    if (
      isLoadingCompositionById ||
      !compositionRaw?.length ||
      isEditorLoadedForThisComposition
    ) {
      return;
    }

    loadAPIComposition(composition);
  }, [composition, isLoadingCompositionById]);

  function handleCopiedText(event: React.ClipboardEvent<HTMLTextAreaElement>) {
    if (shouldBlockCopyPasteActions) {
      event.preventDefault();
      return;
    }

    const textarea = event.target as HTMLTextAreaElement;
    const selectedText = textarea.value.substring(
      textarea.selectionStart,
      textarea.selectionEnd,
    );

    if (selectedText.length > 0) {
      trackEvent('copied_selected_text_in_writing', {
        ...eventDataRef.current,
        supportingContent: {
          selected_text: selectedText,
        },
      });
    }
  }

  useEffect(() => {
    if (composition.size && composition.get('id') !== Number(composition_id)) {
      fetchCompositionByIdRequest(composition_id);
      setReloadingCompositionId(composition_id);
    }
  }, [composition_id]);

  useEffect(() => {
    if (toggleStatus === 'OCR') {
      modals.submitOCR?.showModal();
    }
  }, [toggleStatus]);

  useEffect(() => {
    if (reloadingCompositionId === String(composition.get('id'))) {
      loadAPIComposition(composition);
      eventDataRef.current = {
        test: {test_id: Number(composition.get('test_id'))},
        composition: {composition_id: composition.get('id')},
        learningPathUserInstance: {
          learning_path_user_instance_id: learningPath?.id,
          step_id: composition.getIn(['step', 'id']),
        },
      };
      setReloadingCompositionId(undefined);
    }
  }, [composition]);

  function loadComposition() {
    checkNetworkStatus({
      onlineCallback: () => loadAPIComposition(composition),
      offlineCallback: () => loadIDBComposition(composition_id),
    });

    setShowLoader(false);
  }

  async function loadIDBComposition(compositionId?: string) {
    const IDBCompositions = await compositionsIDB.getAll(compositionId);

    const lastModifiedComposition = IDBCompositions?.reduce(
      (lastModified, current) => {
        return (current.modified ?? 0) > (lastModified.modified ?? 0)
          ? current
          : lastModified;
      },
      {},
    );

    setUserComposition(lastModifiedComposition);

    localVersionCounter.setInitialValue(lastModifiedComposition?.version ?? 0);
  }

  function loadAPIComposition(
    composition: ImmutableMap<CompositionDetailExtended>,
  ) {
    if (composition?.get('composition_raw')?.length) {
      // Override rendered composition with online version if exists
      setUserComposition({
        id: composition.get('id'),
        title: composition.get('title'),
        text: composition.get('composition_raw').replace(/\n\n/gm, '\n\t'),
        modified: new Date(composition.get('modified')).getTime(),
      });
    }

    if (composition.get('status') !== 'finished') {
      compareOnlineOfflineComposition();
    }
  }

  function startAutoSave(composition: UserComposition) {
    stopAutoSave();
    const autoSaveDelay = isDynamicEditor ? 2000 : 10000;

    const autoSaveTimeout = setTimeout(() => {
      saveComposition(composition);
    }, autoSaveDelay);

    setAutoSaveTimer(autoSaveTimeout);
  }

  function stopAutoSave() {
    if (autoSaveTimer) {
      clearTimeout(autoSaveTimer);
    }
  }

  function fetchStatistics() {
    if (
      (isDynamicEditor || composition.get('flag_finished')) &&
      !isLoadingCompositionsJsonStatsAsync
    ) {
      fetchCompositionJsonStatsAsyncRequest(composition.toJS(), {
        compositionRaw: userComposition?.text ?? '',
        markersToProcess: (
          composition.getIn(['genre', 'shown_stats'])?.toJS() ?? []
        ).map((shownStat: any) => shownStat?.marker?.varname),
        setMistakesHighlight: true,
        fromMobile: isMobile,
      });
    }
  }

  function onBeforeUnload(event: BeforeUnloadEvent) {
    // During Focus Mode, suppress the native dialog entirely.
    // FocusModeProvider handles focus_mode_closed via its own beforeunload listener.
    // Uses isActiveRef to avoid stale closure (this fn is captured in useEffect([])).
    if (isActiveRef.current) return;

    // TODO: this message is not beeing printed on the dialog
    event.preventDefault();

    const confirmationMessage =
      'Seus redação ainda não foi terminada. Quer sair mesmo assim?';
    event.returnValue = confirmationMessage;

    return confirmationMessage;
  }

  function handlePastedText(event: React.ClipboardEvent<HTMLTextAreaElement>) {
    const text = filterAllowedUnicodeCharsFromText(
      event.clipboardData.getData('text'),
    );

    if (text.length) {
      debouncedTrackEvent('pasted_text_in_writing', {
        ...eventDataRef.current,
        supportingContent: {
          selected_text: text,
        },
      });
    }
  }

  function handleTypedText(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Backspace') {
      backspaceCounter.sum();
    }
  }

  function onChangeTitle(event: React.ChangeEvent<HTMLTextAreaElement>) {
    const title = event.currentTarget.value;

    const updatedComposition = {...userComposition, title};
    setUserComposition(updatedComposition);
    startAutoSave(updatedComposition);
  }

  function onChangeText(event: React.ChangeEvent<HTMLTextAreaElement>) {
    if (isMobile) {
      changeToggleCompletedTestReportStatus(null);
    }

    const isAddingText =
      event.target.value.length > (userComposition?.text?.length ?? 0);

    const updatedComposition = {
      ...userComposition,
      text: isAddingText
        ? filterAllowedUnicodeCharsFromText(event.target.value)
        : event.target.value,
    };

    setUserComposition(updatedComposition);
    startAutoSave(updatedComposition);
  }

  function handleIndentation(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    const editor = event.target as HTMLTextAreaElement;
    const {selectionStart: carretStart} = editor;
    const textBeforeCarret = editor.value?.slice(0, carretStart);
    const textAfterCarret = editor.value?.slice(carretStart);

    if (event.key === 'Enter') {
      event.preventDefault();
      const indentedText = `${textBeforeCarret}\n\t${textAfterCarret}`;

      setUserComposition({...userComposition, text: indentedText});

      // Because setState is asynchronous, the carret needs to be manually shifted after the re-render
      setTimeout(() => {
        editor.selectionStart = carretStart + 2; // 2 is for adding \n and \t
        editor.selectionEnd = carretStart + 2;
      }, 0);
    }

    if (event.key === 'Backspace' && textBeforeCarret.endsWith('\n\t')) {
      event.preventDefault();

      // Erase indentation and new line to merge current line with previous one
      setUserComposition({
        ...userComposition,
        text: textBeforeCarret.slice(0, -2) + textAfterCarret,
      });

      // Because setState is asynchronous, the carret needs to be manually shifted after the re-render
      setTimeout(() => {
        editor.selectionStart = carretStart - 2; // 2 is for removing \n and \t
        editor.selectionEnd = carretStart - 2;
      }, 0);
    }
  }

  async function finishComposition(sendToManualReview?: boolean) {
    stopAutoSave();
    trackEvent('confirmed_composition_submission', eventDataRef.current);
    trackTimeSpent();

    if (!isCompositionFinished) {
      const accessSource = await getAccessSource();
      saveOffline({...userComposition, finished: true});

      modals.confirmation?.close();
      modals.saveComposition?.showModal();

      uploadComposition.trigger(
        {
          id: Number(composition_id),
          composition_source: accessSource,
          title: userComposition?.title ?? '',
          composition_raw: userComposition?.text ?? '',
          review_type: sendToManualReview ? 'Human' : 'AI-Structure',
          uses_ai_comp: composition.getIn(['test', 'uses_ai_comp']) || isFuvest,
        },
        {
          onError: () => {
            showErrorMessage();
            // Revert the IDB entry written by saveOffline above — patch it in-place
            // so it no longer blocks the editor after a refresh or offline check.
            const latestLocalKey = `${composition_id}-V${localVersionCounter.total()}`;
            compositionsIDB.updateItem(latestLocalKey, {finished: false});
            setUserComposition((prev) =>
              prev ? {...prev, finished: false} : prev,
            );
          },
          onSuccess: () => {
            showSuccessMessage();
          },
        },
      );
    }
  }

  async function saveComposition(userComposition?: UserComposition) {
    if (userComposition) {
      stopAutoSave();

      saveOffline(userComposition);

      const accessSource = await getAccessSource();

      writeCompositionRequest({
        id: composition_id,
        title: userComposition?.title ?? '',
        composition_raw: userComposition?.text ?? '',
        composition_source: accessSource,
        step_number: learningPath?.step_number ?? null,
      });

      fetchStatistics();
    }
  }

  function saveOffline(userComposition: UserComposition) {
    const version = localVersionCounter.sum();
    const modifiedAt = new Date().getTime();

    if (version >= 5) {
      localVersionCounter.reset();
    }

    setUserComposition({
      ...userComposition,
      modified: modifiedAt,
    });

    compositionsIDB.setItem(`${composition_id}-V${version}`, {
      ...userComposition,
      id: composition_id,
      modified: modifiedAt,
      version,
    });
  }

  function recoverComposition(localComposition: UserComposition) {
    setUserComposition(localComposition);
    modals.recoverComposition?.close();

    saveComposition(localComposition);
  }

  async function compareOnlineOfflineComposition() {
    const localCompositions = await compositionsIDB.getAllSortedBy(
      composition_id,
      'modified',
    );
    const lastLocalComposition = localCompositions.shift();

    if (lastLocalComposition) {
      const apiModifiedDate = new Date(composition.get('modified')).getTime();

      if (lastLocalComposition.modified! > apiModifiedDate) {
        modals.recoverComposition?.showModal();
      }
    }
  }

  function showErrorMessage() {
    modals.saveCompositionError?.showModal();

    trackEvent('composition_save_error', {
      ...eventDataRef.current,
      error: {error: uploadComposition.error?.message},
    });
  }

  function showOptionalRewriteModal() {
    const isRewrite = !!composition.get('parent_id');
    const testIsRequired = composition.getIn(['test', 'is_required']);
    const sessionKey = `optional_rewrite_modal_shown_${composition.get('id')}`;
    const alreadyShown = sessionStorage.getItem(sessionKey);

    if (isRewrite && testIsRequired === false && !alreadyShown) {
      modals.optionalRewrite?.showModal();
    }
  }

  function handleCloseOptionalRewrite() {
    const sessionKey = `optional_rewrite_modal_shown_${composition.get('id')}`;
    sessionStorage.setItem(sessionKey, 'true');
    modals.optionalRewrite?.close();
  }

  function showSuccessMessage() {
    if (iLetrusType) {
      modals.iLetrus?.showModal();
    } else {
      modals.saveComposition?.showModal();
    }
  }

  return (
    <>
      {showLoader ? (
        <PageLoading />
      ) : (
        <div className={styles.wrapper}>
          <AiUsageAlert />

          <CompositionHeader
            childId={composition.get('child_id')}
            parentId={composition.get('parent_id')}
            testName={composition.getIn(['test', 'name'])}
            isCompositionFinished={isCompositionFinished}
            isOffline={isOffline}
            isReviewGridMultigenre={isReviewGridMultigenre}
            learningPathUserInstanceId={learningPathUserInstanceId}
            usesAiComp={composition.getIn(['test', 'uses_ai_comp'])}
            isActive={isActive}
            remainingSeconds={remainingSeconds}
            effectiveTimeSeconds={effectiveTimeSeconds}
            isExpired={isExpired}
            editorIsEmpty={editorIsEmpty}
            onSubmitCallback={() => {
              modals.confirmation?.showModal();
              trackEvent('clicked_on_composition_submission', {
                composition: {
                  composition_id: Number(composition.get('id')),
                },
                test: {
                  test_id: Number(composition.getIn(['test', 'id'])),
                },
                learningPathUserInstance: {
                  learning_path_user_instance_id: Number(
                    composition.get('learning_path_user_instance_id'),
                  ),
                  step_id: composition.getIn(['step', 'id']),
                },
              });
            }}
          />

          <main className={styles.composition}>
            <section className={styles.compositionWrapper}>
              <header className={styles.compositionHeader}>
                <SidebarButtons
                  eventData={eventDataRef.current}
                  composition={composition}
                  allowManuscriptSubmission={
                    learningPath?.allow_manuscript_submission
                  }
                />

                <CompositionInfo
                  composition={composition}
                  userComposition={userComposition}
                  onRestoreClick={() => modals.recoverComposition?.showModal()}
                  onManualSaveClick={() => saveComposition(userComposition)}
                  onTimesUpCallback={() =>
                    redirectCompositionByStatus(uploadComposition.data)
                  }
                />
              </header>

              <article className={styles.compositionText}>
                <Textarea
                  title="título"
                  className={styles.compositionTitle}
                  name="title"
                  aria-label="Título da Redação"
                  value={userComposition?.title}
                  placeholder="Adicione um título"
                  onChange={(title) => onChangeTitle(title)}
                  autoComplete="off"
                  spellCheck="false"
                  tabIndex={0}
                  maxLength={200}
                  readOnly={isCompositionFinished || isOffline}
                />

                <Textarea
                  className={styles.compositionText}
                  name="composition"
                  value={userComposition?.text}
                  onChange={(text) => onChangeText(text)}
                  onPaste={(
                    event: React.ClipboardEvent<HTMLTextAreaElement>,
                  ) => {
                    if (shouldBlockCopyPasteActions) {
                      event.preventDefault();

                      return;
                    }

                    handlePastedText(event);
                  }}
                  onCopy={(event: React.ClipboardEvent<HTMLTextAreaElement>) =>
                    handleCopiedText(event)
                  }
                  onCut={(event: React.ClipboardEvent<HTMLTextAreaElement>) => {
                    if (shouldBlockCopyPasteActions) {
                      event.preventDefault();
                    }
                  }}
                  onKeyUp={(event) => handleTypedText(event)}
                  onKeyDown={(event) => handleIndentation(event)}
                  placeholder={
                    isActive
                      ? 'Digite sua redação aqui'
                      : 'Digite (ou cole) sua redação aqui'
                  }
                  autoComplete="off"
                  spellCheck="false"
                  autoCorrect="off"
                  cacheMeasurements
                  readOnly={isCompositionFinished || isOffline}
                  maxLength={MAX_EDITOR_NUMBER_OF_CHARACTERS}
                />
              </article>
            </section>

            {!modals.submitOCR?.open && (
              <CompositionSidebar
                learningPath={learningPath}
                composition={composition}
              />
            )}

            <Dialog
              open={
                !!composition?.get('composition_raw')?.length &&
                isOpenReminderCompositionReview
              }
            >
              <div className={styles.reminderCompositionReviewContainer}>
                <ChatBubble
                  imageUrl={handRaisedWithoutWaving}
                  variant="character-bottom"
                  message={`A revisão é uma parte essencial! Verifique se tudo foi transcrito corretamente e edite antes de enviar.
                
              Antes de enviar, lembre-se de:

              - Conferir se o texto está completo.
              - Corrigir palavras e letras que possam ter saído erradas.
              - Remover elementos que não fazem parte da redação, como datas, emojis ou desenhos.`}
                  clickBlocked
                  onCloseButton={() =>
                    setIsOpenReminderCompositionReview(false)
                  }
                />
              </div>
            </Dialog>
          </main>
        </div>
      )}

      <SubmitOCRModal
        id="submitOCR"
        composition={composition}
        onClose={() => {
          modals.submitOCR?.close();
          fetchCompositionByIdRequest(composition_id);
          setIsOpenReminderCompositionReview(true);
        }}
      />

      <FinishCompositionModal
        id="confirmation"
        isCheckReviewReminderModal={false}
        onConfirmFinish={() => finishComposition()}
        onClose={() => modals.confirmation?.close()}
      />

      <SubmitCompositionModal
        id="saveComposition"
        loading={!!uploadComposition.isMutating}
        onClose={() => modals.saveComposition?.close()}
        onClick={() => {
          if (
            learningPath?.uses_focus_mode &&
            !isFocusModeFeedbackReleased(learningPath)
          ) {
            fetchCompositionByIdRequest(composition_id);
            navigateToLearningPath.nextStep(Number(composition_id));
          } else {
            redirectCompositionByStatus(uploadComposition.data);
          }
        }}
        hasReviewError={composition?.get('error')?.includes('gerar correção')}
      />

      <GeneralErrorModal
        id="saveCompositionError"
        title="Atenção!"
        showIcon={false}
        subTitle={uploadComposition.error?.message}
        description={uploadComposition.error?.cause as string}
      />

      <ILetrusModal
        id="iLetrus"
        finishedTimer={false}
        iLetrusType={iLetrusType}
        composition={composition}
        userText={userComposition?.text}
        onClick={() => redirectCompositionByStatus(uploadComposition.data)}
      />

      <RecoverCompositionModal
        id="recoverComposition"
        onClose={() => modals.recoverComposition?.close()}
        onClickRestore={(composition) => recoverComposition(composition)}
      />

      <OptionalRewriteModal
        id="optionalRewrite"
        onClose={handleCloseOptionalRewrite}
      />
    </>
  );
};

export default connect(
  (state: ApplicationState) => ({
    composition: getCompositionById(state),
    isLoadingCompositionById: getIsLoadingCompositionById(state),
    isLoadingCompositionsJsonStatsAsync:
      isLoadingCompositionsJsonStatsAsync(state),
    toggleStatus: getToggleStatus(state),
  }),
  {
    writeCompositionRequest,
    fetchCompositionByIdRequest,
    changeToggleCompletedTestReportStatus,
    fetchCompositionJsonStatsAsyncRequest,
  },
)(EditorPage);
